import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Package, Home, Clock, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { paymentAPI, orderAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { SEO } from '../seo/SEO';
import * as meta from '../analytics/meta';

const OrderSuccessPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const isCod = searchParams.get('cod') === 'true';
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  // Whether the initial confirmation has completed enough to hide the
  // "confirming payment" spinner. For COD this is instant. For Stripe we
  // don't gate the whole page on the polling — see checkPaymentAndOrder.
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  useEffect(() => {
    if (isCod && orderId) {
      // COD order - fetch order directly
      fetchCodOrder();
    } else if (sessionId) {
      // Card payment - kick off background polling but DON'T block the
      // "Order Received" page render on it — see req 1 (Stripe flow):
      // the Thank You page must appear immediately once we have any
      // signal from Stripe / the order lookup.
      checkPaymentAndOrder();
    } else {
      // No session ID or order ID, just show success
      setInitialCheckDone(true);
      setLoading(false);
    }
  }, [sessionId, orderId, isCod]);

  // Fire the Meta Pixel `Purchase` event whenever we have a confirmed
  // order in hand. The `meta.purchase()` helper is dedupe-guarded by
  // `order.id` in sessionStorage so a re-render or a page refresh won't
  // double-report the conversion to Ads Manager.
  useEffect(() => {
    if (order && order.id) {
      meta.purchase(order);
    }
  }, [order]);

  const fetchCodOrder = async () => {
    try {
      const orderResponse = await orderAPI.getOrder(orderId);
      setOrder(orderResponse.data);
      setPaymentConfirmed(true);
    } catch (error) {
      console.error('Error fetching COD order:', error);
    } finally {
      setInitialCheckDone(true);
      setLoading(false);
    }
  };

  const checkPaymentAndOrder = async () => {
    // Render the Thank You page as soon as we've completed the FIRST
    // check — even if the payment_status still shows "pending" the
    // customer's card has already been charged by Stripe and the
    // webhook will catch up within a few seconds. See req 1 + 2:
    // printer terminology never surfaces here.
    const maxAttempts = 10;
    let currentAttempt = 0;

    const poll = async () => {
      try {
        const paymentResponse = await paymentAPI.getPaymentStatus(sessionId);
        const paid = paymentResponse.data.payment_status === 'paid';
        if (paid) setPaymentConfirmed(true);

        // Best-effort order fetch — non-fatal.
        try {
          const orderResponse = await orderAPI.getOrderBySession(sessionId);
          setOrder(orderResponse.data);
        } catch (_e) { /* ignore */ }

        // On the FIRST tick, unblock the page render regardless of
        // paid state. Continue polling in the background purely to
        // update the on-screen order-status badge.
        if (!initialCheckDone) {
          setInitialCheckDone(true);
          setLoading(false);
        }

        if (paid) return;
        if (currentAttempt < maxAttempts) {
          currentAttempt += 1;
          setTimeout(poll, 2000);
        } else {
          // Timed out waiting for webhook — Stripe usually confirms by now.
          setPaymentConfirmed(true);
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (!initialCheckDone) { setInitialCheckDone(true); setLoading(false); }
        if (currentAttempt < maxAttempts) {
          currentAttempt += 1;
          setTimeout(poll, 2000);
        } else {
          setPaymentConfirmed(true);
        }
      }
    };
    poll();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Confirming your payment...</p>
          <p className="text-sm text-muted-foreground mt-2">This may take a few seconds</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-green-50 to-white">
      <SEO title="Order confirmed" noIndex includeRestaurantSchema={false} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-green-100">
          {/* Success Icon */}
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-6 flex justify-center"
          >
            <div className="bg-green-100 rounded-full p-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
          </motion.div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-center mb-2" data-testid="order-received-heading">
            Thank you!
          </h1>
          <p className="text-lg text-center text-neutral-700 mb-2">
            Your order has been received.
          </p>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {isCod
              ? "We're sending it to the restaurant now. Please have your cash ready on delivery."
              : "We're sending it to the restaurant now."}
          </p>

          {/* Order Details Card */}
          {order && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-gray-50 rounded-xl p-4 mb-6"
            >
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-5 w-5 text-primary" />
                <span className="font-semibold">Order Details</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Order Number</span>
                  <span className="font-bold text-xl text-primary">
                    {order.daily_order_number 
                      ? `#${String(order.daily_order_number).padStart(3, '0')}`
                      : `#${order.id?.slice(-6).toUpperCase()}`
                    }
                  </span>
                </div>
                {order.order_date && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span>{order.order_date}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-semibold text-primary">£{order.total_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type</span>
                  <span className="capitalize">{order.order_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment</span>
                  <span className={`font-medium ${
                    order.payment_method === 'cod' || order.payment_method === 'coc'
                      ? 'text-orange-600'
                      : 'text-green-600'
                  }`}>
                    {order.payment_method === 'coc'
                      ? 'Cash on Collection'
                      : order.payment_method === 'cod'
                        ? 'Cash on Delivery'
                        : 'Paid by Card'}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {/* COD Payment Reminder */}
          {isCod && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="8" r="6" />
                <path d="M18.09 10.37A6 6 0 1 1 10.34 18" />
                <path d="M7 6h1v4" />
                <path d="m16.71 13.88.7.71-2.82 2.82" />
              </svg>
              <p className="text-sm text-orange-800">
                <strong>Cash on Delivery:</strong> Please have £{order?.total_amount?.toFixed(2) || '0.00'} ready when your order arrives.
              </p>
            </motion.div>
          )}

          {/* Email Notification */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="flex items-center gap-3 bg-blue-50 rounded-xl p-4 mb-6"
          >
            <Mail className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <p className="text-sm text-blue-700">
              A confirmation email has been sent to your email address.
            </p>
          </motion.div>

          {/* Estimated Time */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-3 bg-orange-50 rounded-xl p-4 mb-6"
          >
            <Clock className="h-5 w-5 text-orange-500 flex-shrink-0" />
            <p className="text-sm text-orange-700">
              Estimated preparation time: <strong>20-30 minutes</strong>
            </p>
          </motion.div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {user && (
              <Button
                data-testid="view-orders-button"
                className="w-full rounded-full h-12"
                onClick={() => navigate('/orders')}
              >
                <Package className="h-4 w-4 mr-2" />
                View My Orders
              </Button>
            )}
            <Button
              data-testid="continue-shopping-button"
              variant={user ? "outline" : "default"}
              className="w-full rounded-full h-12"
              onClick={() => navigate('/')}
            >
              <Home className="h-4 w-4 mr-2" />
              {user ? 'Continue Shopping' : 'Back to Menu'}
            </Button>
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-muted-foreground mt-6">
            Questions about your order? Contact us at the restaurant.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default OrderSuccessPage;
